/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box} from '@material-ui/core';
import InputLabel from '@material-ui/core/InputLabel';
import Popover from '@material-ui/core/Popover';
import {makeStyles} from '@material-ui/core/styles';
import ClockIcon from '@material-ui/icons/AccessTime';
import classnames from 'classnames';
import {
  addMonths,
  isSameDay,
  isWithinInterval,
  setYear
} from 'date-fns';
import isNil from 'lodash.isnil';
import isString from 'lodash.isstring';
import moment, {Moment} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  formatIfValid,
  isValid,
  isValidDateTime,
  resolveDateInput,
  twelveHourTime,
  VALID_MANUAL_DATE_FORMATS,
  VALID_MANUAL_DATE_TIME_FORMATS
} from '@ion/core/src/utils/DateUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {DateOutlinedIcon} from '../../../src/icons/DateOutlinedIcon';
import {NotificationCalendarIcon} from '../../../src/icons/NotificationCalendarIcon';
import {TimeOutlinedIcon} from '../../../src/icons/TimeOutlinedIcon';
import {TertiaryButton} from '../../components/buttons/TertiaryButton';
import TextField from '../../components/textfield/TextField';
import getGlobalProp from '../../props/GlobalPropsConfig';
import {toggleThemeStyle} from '../../theme/helpers';
import {
  dlsAlt,
  dlsColor,
  dlsTypography,
  dlsZIndex,
  getStyleClass
} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import RenderDatePicker from './RenderDatePicker';

export interface DatePickerTextConfig {
  useDatePickerTextField?: boolean;
  validDateTimeFormats?: string[];
  validTimeFormats?: string[];
  validDateFormats?: string[];
  allowManualEntry?: boolean;
  isCurrentTheme?: boolean;
  allowManualDateEntry?: boolean;
};

interface StyleProps extends DatePickerTextConfig {
  fieldLabel?: string;
  overridePopupStyle?: GenericObject;
  externalClasses?: GenericObject;
};

export interface CurrentDayProps {
  isNow?: boolean;
  isToday?: boolean;
};

// defines the prop definitions for the component
export interface DatePickerProps extends StyleProps, CurrentDayProps {
  // closes the calendar when a parent controlling it passes `false` (e.g.
  // when another of its pickers opens); left out, the picker opens and
  // closes itself ...
  open?: boolean;
  toggle?: () => void;
  onChange?: (date: Date | Moment | string, required: boolean) => void;
  onClose?: () => void;
  closeOnClickOutside?: boolean;
  wrapperClassName?: string;
  value?: Date;
  maxDate?: Date;
  minDate?: Date;
  isDate?: boolean;
  isTime?: boolean;
  isDateTime?: boolean;
  defaultValue?: Date | string;
  placeholder?: string;
  hasError?: boolean;
  errorText?: string;
  required?: boolean;
  disabled?: boolean;
  manualInput?: boolean;
  isClock?: boolean;
  isDatePicker?: boolean;
  customError?: boolean;
  customLabel?: boolean;
  validDateTimeFormats?: string[];
  validTimeFormats?: string[];
  hideClearButton?: boolean;
  alternateInputStyle?: boolean;
  popoverOffset?: number | string;
  includeContainerStyles?: boolean;
};

type DatePickerErrorState = {
  hasError: boolean;
  message: string
};

interface DatePickerTextOptions {
  allowManualEntry: boolean;
  isCurrentTheme: boolean;
  isDateTime: boolean;
  isTime: boolean;
  value: Date | string;
}

interface DateWithTimeOptions {
  date: Date;
  hours: number;
  minutes: number;
}

interface LegacyManualDateOptions {
  allowManualEntry: boolean;
  isCurrentTheme: boolean;
  validDateTimeFormats: string[];
  value?: Date | string;
}

interface ManualInputDateOptions {
  format: string[];
  inputTime: string;
  isLegacyManualDatePicker: boolean;
}

const getNativeTwelveHourTime = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${minutes} ${meridiem}`;
};

const getDateWithTime = ({
  date,
  hours,
  minutes
}: DateWithTimeOptions): Date => {
  const selectedDate = new Date(date);
  selectedDate.setHours(hours, minutes, 0, 0);
  return selectedDate;
};

const getLegacyManualDate = ({
  allowManualEntry,
  isCurrentTheme,
  validDateTimeFormats,
  value
}: LegacyManualDateOptions): Date | null => {
  if (!allowManualEntry || isCurrentTheme || !isString(value)) {
    return value ? new Date(value) : null;
  }
  const parsedDate = moment(value, validDateTimeFormats, true);
  return parsedDate.isValid() ? parsedDate.toDate() : new Date(value);
};

const getLegacyManualDateText = (
  allowManualEntry: boolean,
  isCurrentTheme: boolean,
  value?: Date | string
): string | null => (
  allowManualEntry && !isCurrentTheme && isString(value) ? value : null
);

const getManualInputDate = ({
  format,
  inputTime,
  isLegacyManualDatePicker
}: ManualInputDateOptions): Date => {
  if (!isLegacyManualDatePicker) {
    return new Date(inputTime);
  }

  const parsedDate = moment(inputTime, format, true);
  return parsedDate.isValid() ? parsedDate.toDate() : new Date(inputTime);
};

const toggleStyle = (manual: string, auto: string, property?: string): any =>
  ({allowManualEntry, overridePopupStyle}: StyleProps): string => (overridePopupStyle && property)
    ? overridePopupStyle?.[property] : allowManualEntry ? manual : auto;
;

const getDatePickerText = ({
  allowManualEntry,
  isCurrentTheme,
  isDateTime,
  isTime,
  value
}: DatePickerTextOptions): string => {
  const val = value ? new Date(value) : new Date();
  const time = isCurrentTheme ? getNativeTwelveHourTime(val) : twelveHourTime(val, 'h:mm A');
  const manualDate = formatIfValid(val, VALID_MANUAL_DATE_FORMATS[0]);
  const useUserLocale = getGlobalProp('date-time.formatter.useUserLocale', true) as boolean;
  const locales = useUserLocale ? 'en-CA' : undefined;
  return value
    ? isDateTime
      ? `${allowManualEntry ? manualDate : val.toLocaleDateString(locales)} ${time}`
      : isTime
        ? time
        : allowManualEntry ? manualDate : val.toLocaleDateString(locales)
    : '';
};

const useStyles = makeStyles(theme => {
  const inputHeight  = ({isCurrentTheme}) => isCurrentTheme ? '' : '35px';
  const inputPadding = ({isCurrentTheme}) => isCurrentTheme ? '8px' : '';
  return ({
    popup : {
      margin                         : toggleStyle('2px 0', '16px'),
      padding                        : toggleStyle('12px', 'auto'),
      position                       : toggleStyle('absolute', 'unset', 'position'),
      backgroundColor                : toggleStyle(dlsColor('white'), ''),
      width                          : toggleStyle('auto', '312px'),
      boxShadow                      : toggleStyle('0px 2px 4px 3px rgb(0 0 0 / 20%)', 'unset'),
      right                          : ({popoverOffset}: DatePickerProps) => popoverOffset || 0,
      [theme.breakpoints.down('sm')] : {
        position : toggleStyle('relative', 'unset')
      }
    },
    'current-popup' : {
      gap                            : '4px',
      width                          : toggleStyle('auto', '312px'),
      margin                         : toggleStyle('2px 0', '16px'),
      display                        : 'flex',
      opacity                        : 1,
      padding                        : toggleStyle('12px', 'auto'),
      position                       : toggleStyle('absolute', 'unset', 'position'),
      boxShadow                      : toggleStyle('0px 4px 4px 0px #00000040', 'unset'),
      paddingTop                     : '32px',
      borderRadius                   : '12px',
      flexDirection                  : 'column',
      backgroundColor                : toggleStyle(dlsColor('white'), ''),
      right                          : ({popoverOffset}: DatePickerProps) => popoverOffset || 0,
      [theme.breakpoints.down('sm')] : {
        position : toggleStyle('relative', 'unset')
      }
    },
    currentPopoverPaper : {
      borderTop    : '2px solid #CCE5E8',
      borderRadius : '12px'
    },
    placeholder : {
      position        : 'relative',
      width           : '130px',
      top             : ({fieldLabel}: {fieldLabel: StyleProps}) => fieldLabel ? '37px' : '20px',
      paddingLeft     : '12px !important',
      zIndex          : dlsZIndex('datePickerPlaceholder'),
      lineHeight      : '15px !important',
      fontSize        : '16px',
      color           : dlsColor('grey4'),
      '&.Mui-focused' : {
        color : dlsColor('grey4')
      }
    },
    fieldLabel : toggleThemeStyle({
      color        : dlsColor('grey2'),
      marginBottom : '8px',
      ...dlsTypography('body3')
    }, {
      color        : dlsColor('grey2'),
      fontSize     : '14px',
      fontFamily   : 'Roboto',
      fontWeight   : 700,
      lineHeight   : '20px !important',
      marginBottom : '8px'
    }),
    requiredAsterisk : {
      color  : toggleThemeStyle(dlsColor('aaBlue'), dlsColor('red')),
      margin : '0 2px'
    },
    disabled : {
      '&' : {
        pointerEvents : 'none !important'
      },
      '&:before' : {
        borderBottomStyle : 'solid !important'
      }
    },
    inputRoot : {
      padding   : '4px 11px 4px 6px !important',
      '&:hover' : {
        backgroundColor : dlsAlt('components', 'textfield', 'hoverBackgroundColor')
      },
      '& input' : {
        height      : inputHeight,
        padding     : inputPadding,
        zIndex      : dlsZIndex('datePickerInputRoot'),
        paddingLeft : ({paddingLeft}: {paddingLeft: string | number}) =>
          paddingLeft ? `${paddingLeft} !important` : '11px'
      }
    },
    underline : {
      backgroundColor : dlsColor('white'),
      '&:after'       : {
        borderColor : dlsColor('caBlue')
      },
      '&:before' : {
        borderColor       : `${dlsColor('grey5')} !important`,
        borderBottomWidth : '1px !important'
      }
    },
    underlineError : {
      backgroundColor : dlsColor('white'),
      '&:after'       : {
        borderColor : `${theme.palette.error.main} !important`
      },
      '&:before' : {
        borderColor       : `${theme.palette.error.main} !important`,
        borderBottomWidth : '1px !important'
      }
    },
    errorText : {
      color : `${theme.palette.error.main}`
    },
    currentErrorText : {
      color : `${dlsColor('red')} !important`
    },
    currentFieldLabelError : {
      color : `${dlsColor('red')} !important`
    },
    container : {
      position : 'relative',
      minWidth : '185px',
      width    : '47%'
    },
    labelContainer : {
      display        : 'flex',
      justifyContent : 'space-between',
      alignItems     : toggleThemeStyle('flex-start', 'center'),
      height         : toggleThemeStyle('auto', '32px')
    },
    ctaLink : {
      height   : '20px',
      fontSize : '12px !important'
    },
    'current-ctaLink' : {
      color         : dlsColor('darkestBlue'),
      height        : '0px !important',
      fontSize      : '13px !important',
      fontFamily    : 'Inter',
      fontWeight    : '600',
      lineHeight    : '14px',
      letterSpacing : '0.5px',
      verticalAlign : 'middle',
      marginBottom  : '6px'
    },
    alternateInputRoot : {
      borderTop   : `1px solid ${dlsColor('grey5')}`,
      borderRight : `1px solid ${dlsColor('grey5')}`,
      borderLeft  : `1px solid ${dlsColor('grey5')}`,
      padding     : '4px 11px 4px 6px !important',
      '&:hover'   : {
        backgroundColor : dlsAlt('components', 'textfield', 'hoverBackgroundColor')
      },
      '& input' : {
        height      : inputHeight,
        padding     : inputPadding,
        zIndex      : dlsZIndex('datePickerInputRoot'),
        paddingLeft : ({paddingLeft}: {paddingLeft: string | number}) =>
          paddingLeft ? `${paddingLeft} !important` : '11px'
      }
    },
    icon : {
      display        : 'flex',
      alignSelf      : 'center',
      justifyContent : 'center',
      cursor         : 'pointer'
    }
  } as any);
});

/**
 * @DatePicker
 *
 * Defines the DatePicker component. It allows us to either choose date, time or choose both simultaneously.
 *
 * ```typescript
 *
 * import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
 *
 * <>
 *    <DatePicker showPicker={showPicker}
 *       // Pass date prop externally. If nothing is passed, current date is selected by default.
 *       value={dateProp}
 *       // Can pass isDate, isTime or isDateTime. If nothing is passed, default will be isDate
 *       isDateTime
 *     />}
 * </>
 *
 * ```
 */
export const DatePicker: FC<DatePickerProps> = ({
  isTime,
  isDateTime,
  toggle,
  fieldLabel,
  placeholder,
  defaultValue,
  hasError,
  errorText,
  required,
  onChange,
  onClose,
  maxDate,
  minDate,
  value,
  externalClasses,
  overridePopupStyle,
  open,
  isNow = false,
  isDate = true,
  isClock = false,
  isToday = false,
  disabled = false,
  manualInput = false,
  isDatePicker = false,
  customError = false,
  customLabel = true,
  useDatePickerTextField = false,
  validDateTimeFormats = VALID_MANUAL_DATE_TIME_FORMATS,
  validTimeFormats = ['h:mm A'],
  allowManualEntry = false,
  hideClearButton = false,
  alternateInputStyle = false,
  includeContainerStyles = false,
  popoverOffset = 0
}: DatePickerProps) => {
  const {isCurrent: isCurrentTheme} = useThemeContext();

  const errorState = {
    hasError : true,
    message  : translate(`common:editors.scheduleEditor.configs.errors.${customError ? 'dateFormat' : 'format'}`)
  };

  const validState   = {hasError: false, message: ''};
  const classes: any = useStyles({
    fieldLabel,
    allowManualEntry,
    overridePopupStyle,
    popoverOffset,
    isCurrentTheme
  });

  const initialDate = getLegacyManualDate({
    allowManualEntry,
    isCurrentTheme,
    validDateTimeFormats,
    value : defaultValue
  });

  const [date, setDate]             = useState<Date>(initialDate);
  const [viewDate, setViewDate]     = useState<Date>(initialDate ?? new Date());
  const [hoverDay, setHoverDay]     = useState<Date>();
  const [anchorEl, setAnchorEl]     = useState<null | HTMLElement>(null);
  const [showPicker, setShowPicker] = useState(false);

  const [dateTime, setDateTime] = useState(() => (
    getLegacyManualDateText(allowManualEntry, isCurrentTheme, defaultValue) ?? getDatePickerText({
      allowManualEntry,
      isCurrentTheme,
      isDateTime,
      isTime,
      value : defaultValue as Date | string
    })
  ));

  const [error, setError]     = useState<DatePickerErrorState>(validState);
  const clearedDefaultTimeRef = useRef<number | null>(null);
  const isTypingRef           = useRef(false);
  const isInvalid             = Boolean(hasError || error.hasError);

  useEffect(() => {
    if (open === false && showPicker) {
      setShowPicker(false);
    }
  }, [open, showPicker]);

  const getClass = useCallback((name: string) => (
    getStyleClass(name, classes, isCurrentTheme)
  ), [isCurrentTheme, classes]);

  const updateDateTime = useCallback((value: Date | string, isDateTime: boolean, isTime: boolean): string => {
    return getDatePickerText({
      allowManualEntry,
      isCurrentTheme,
      isDateTime,
      isTime,
      value
    });
  }, [allowManualEntry, isCurrentTheme]);

  useEffect(() => {
    if (!showPicker) {
      const sourceDateValue = value ?? defaultValue;
      if (isNil(sourceDateValue) || sourceDateValue === '') {
        // a nil value echoed back from our own partial/invalid typing must not wipe the text...
        if (!isTypingRef.current) {
          setDate(null);
          setViewDate(new Date());
          setDateTime('');
        }
      } else {
        isTypingRef.current = false;
        const legacyManualText = getLegacyManualDateText(allowManualEntry, isCurrentTheme, sourceDateValue);
        const nextDate = getLegacyManualDate({
          allowManualEntry,
          isCurrentTheme,
          validDateTimeFormats,
          value : sourceDateValue
        });
        const isClearedDefault = isValid(nextDate) && isNil(value) &&
          clearedDefaultTimeRef.current === nextDate.getTime();
        if (isValid(nextDate) && !isClearedDefault) {
          const nextDateTime = legacyManualText ?? updateDateTime(nextDate, isDateTime, isTime);
          setDate(previousDate => previousDate?.getTime() === nextDate.getTime()
            ? previousDate
            : nextDate);
          setViewDate(previousDate => previousDate?.getTime() === nextDate.getTime()
            ? previousDate
            : nextDate);
          setDateTime(previousDateTime => previousDateTime === nextDateTime
            ? previousDateTime
            : nextDateTime);
        }
      }
    }
  }, [
    allowManualEntry,
    defaultValue,
    isDateTime,
    isTime,
    isCurrentTheme,
    showPicker,
    updateDateTime,
    validDateTimeFormats,
    value
  ]);

  const onInputChange = (inputTime: string) => {
    const time = isTime
      ? `${formatIfValid(resolveDateInput(new Date()), VALID_MANUAL_DATE_FORMATS[0])} ${inputTime}`
      : inputTime;
    const isEmptyInput = isEmpty(inputTime);
    const format       = !isDatePicker ? validTimeFormats : validDateTimeFormats;
    const isLegacyManualDatePicker = allowManualEntry && !isCurrentTheme && isDatePicker;
    const toDate       = getManualInputDate({
      format,
      inputTime : time,
      isLegacyManualDatePicker
    });
    const valid        = isValidDateTime(inputTime, format) || isEmptyInput;
    const date         = isValid(toDate) && valid
      ? isLegacyManualDatePicker ? toDate : resolveDateInput(time)
      : toDate;
    const errorMsg     = format.join(` ${translate('common:conjunctions.or')} `);
    const isInvalid    = !isEmptyInput && (!valid || !isValid(toDate));
    const error        = isInvalid ? (
      isDatePicker ? {hasError: true, message: errorMsg} : errorState
    ) : validState;
    isTypingRef.current = !isEmptyInput && isInvalid;
    setDateTime(inputTime);
    setError(error);
    onChange?.((!isInvalid && !isEmptyInput ? date : null), isInvalid);
    isValid(toDate) && valid && setDate(date);
  };

  function handleChange({target}: React.ChangeEvent<HTMLInputElement>) {
    manualInput && onInputChange(target?.value);
  }

  function handleClickAway(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(anchorEl ? null : event?.currentTarget);
    setShowPicker(false);
    onClose?.();
  };

  const onDayClick = (day: Date, hours: number, minutes: number) => {
    const d = isCurrentTheme
      ? getDateWithTime({
        date : day,
        hours,
        minutes
      })
      : moment(day).set({
        hour        : hours,
        millisecond : 0,
        minute      : minutes,
        second      : 0
      }).toDate();
    setHoverDay(d);
    setDate(d);
    setViewDate(d);
    onChangeDate(d);
  };

  const onNavigate = (action: number, func: Function) => {
    if (isCurrentTheme) {
      const sourceDate = date || viewDate;
      const nextDate = func(sourceDate, action);
      setViewDate(nextDate);
      if (date) {
        onDayClick(nextDate, date.getHours(), date.getMinutes());
      }
    } else {
      // legacy selects the navigated date, as before - the field and its
      // onChange follow the month and year picked in the header ...
      const nextDate = func(date || viewDate, action);
      setViewDate(nextDate);
      setDate(nextDate);
      onChangeDate(nextDate);
    }
  };

  const onMonthNavigate = (action: number) => {
    onNavigate(action, addMonths);
  };

  const onYearNavigate = (action: number) => {
    onNavigate(action, setYear);
  };

  const onDayHover = (date: Date) => {
    if (!hoverDay || !isSameDay(date, hoverDay)) {
      setHoverDay(date);
    }
  };

  const setTextField = useCallback((event) => {
    setAnchorEl(event?.currentTarget);
    if (!allowManualEntry && !disabled) {
      setShowPicker(useDatePickerTextField);
    }
    toggle?.();
  }, [useDatePickerTextField, allowManualEntry, disabled]);

  const onChangeDate = useCallback((date) => {
    if (isNil(date)) {
      const defaultDate = defaultValue ? new Date(defaultValue) : null;
      clearedDefaultTimeRef.current = defaultDate && isValid(defaultDate)
        ? defaultDate.getTime()
        : null;
      setDateTime('');
      onChange?.(null, false);
      setError(validState);
    } else {
      clearedDefaultTimeRef.current = null;
      let dateField         = updateDateTime(date, isDateTime, isTime);
      const isValidInterval = minDate && maxDate && !isNil(minDate) && !isNil(maxDate);
      const isOutOfRange    = isValidInterval ? !isWithinInterval(date, {start: minDate, end: maxDate}) : false;
      if (!isOutOfRange) {
        if (allowManualEntry && !isCurrentTheme) {
          const formatted   = formatIfValid(date, validDateTimeFormats?.[0]);
          const isValidDate = isDatePicker ? isValidDateTime(formatted, validDateTimeFormats)
            : (isValidDateTime(formatted, validTimeFormats) || isEmpty(formatted));
          isValid(formatted) && isValidDate && (dateField = formatted);
        }
        setDateTime(dateField);
        onChange?.(date, false);
        setError(validState);
      }
    }
  }, [
    allowManualEntry,
    defaultValue,
    isDatePicker,
    isDateTime,
    isTime,
    isCurrentTheme,
    maxDate,
    minDate,
    onChange,
    updateDateTime,
    validDateTimeFormats,
    validState,
    validTimeFormats
  ]);

  const onClickNow = useCallback(() => {
    const currentDateTime = isCurrentTheme ? new Date() : resolveDateInput(new Date());
    if (isCurrentTheme) {
      setDate(currentDateTime);
      setViewDate(currentDateTime);
    }
    onChangeDate(currentDateTime);
  }, [isCurrentTheme, onChangeDate]);

  const handlers = {
    onDayClick,
    onDayHover,
    onMonthNavigate,
    onYearNavigate,
    onInputChange,
    onChangeDate
  };

  const datePicker = useMemo(() => (
    <div className={getClass('popup')}>
      <RenderDatePicker date={isCurrentTheme ? viewDate : date}
        selectedDate={isCurrentTheme ? date : undefined}
        allowManualEntry={allowManualEntry}
        manualInput={dateTime}
        isCurrentTheme={isCurrentTheme}
        handleClickAway={handleClickAway}
        isDate={isTime ? false : isDate}
        isDateTime={isDateTime}
        isTime={isTime}
        setDate={setDate}
        handlers={handlers}
        minDate={minDate}
        maxDate={maxDate}
        value={value}
        hideClearButton={hideClearButton} />
    </div>
  ), [
    date,
    viewDate,
    allowManualEntry,
    dateTime,
    getClass,
    handleClickAway,
    handlers,
    isDate,
    isTime,
    isDateTime,
    isCurrentTheme,
    hideClearButton,
    setDate,
    minDate,
    maxDate,
    value
  ]);

  return (
    <div className={classnames({
      [classes.container]          : includeContainerStyles,
      [externalClasses?.container] : !!externalClasses?.container
    }
    )}>
      {fieldLabel &&
        <div className={classes.labelContainer}>
          <div className={classnames(classes.fieldLabel, {
            [externalClasses?.fieldLabel]    : isCurrentTheme && !!externalClasses?.fieldLabel,
            [classes.currentFieldLabelError] : isCurrentTheme && isInvalid
          })}>
            {fieldLabel}
            {required && <span className={classes.requiredAsterisk}>*</span>}
          </div>
          {(isNow || isToday) && (
            <TertiaryButton className={getClass('ctaLink')} onClick={onClickNow}>
              {translate(`common:time.${isNow ? 'now' : 'today'}`)}
            </TertiaryButton>
          )}
        </div>
      }
      {placeholder && customLabel && !fieldLabel && !dateTime && (
        <InputLabel className={classes.placeholder}
          disableAnimation
          shrink={false}>
          {placeholder}
          {required && !fieldLabel && <span className={classes.requiredAsterisk}>*</span>}
        </InputLabel>
      )}
      <TextField value={(dateTime || '')}
        disabled={disabled}
        placeholder={!customLabel ? placeholder : ''}
        onClick={setTextField}
        onChange={handleChange}
        helperText={(hasError && errorText) || (error.hasError && error.message)}
        {...(isCurrentTheme && {
          hasError  : isInvalid,
          errorText : (hasError && errorText) || (error.hasError && error.message)
        })}
        FormHelperTextProps={{
          className : classnames(classes.errorText, {[classes.currentErrorText]: isCurrentTheme})
        }}
        inputProps={fieldLabel && {placeholder}}
        InputProps={{
          endAdornment : (
            <Box className={classes.icon} onClick={function(event) {
              setAnchorEl(event?.currentTarget);
              setShowPicker(!showPicker);
            }}>
              {alternateInputStyle ? (
                isClock ? <TimeOutlinedIcon /> : <DateOutlinedIcon />
              ) : (
                isClock ? <ClockIcon /> : <NotificationCalendarIcon />
              )}
            </Box>),
          ...(!isCurrentTheme && {
            classes : {
              root      : alternateInputStyle ? classes.alternateInputRoot : classes.inputRoot,
              underline : hasError || error.hasError ? classes.underlineError : classes.underline,
              disabled  : classes.disabled
            }
          })
        }} />
      {showPicker && (
        !allowManualEntry ? (
          <Popover anchorEl={anchorEl}
            style={{marginLeft: '10px'}}
            open={showPicker}
            anchorReference='anchorEl'
            {...(isCurrentTheme && {
              PaperProps : {
                classes : {root: classes.currentPopoverPaper}
              }
            })}
            anchorOrigin={{
              vertical   : 'bottom',
              horizontal : 'right'
            }}
            transformOrigin={{
              vertical   : 'top',
              horizontal : 'right'
            }}>
            {datePicker}
          </Popover>
        ) : (
          datePicker
        )
      )}
    </div>
  );
};

export default DatePicker;
